import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// NOTE: View mode persists across navigation within a session.
// Only resets to path-based default on full page refresh (useState initializer).

export type ViewMode = 'user' | 'admin';

interface ViewModeContextValue {
  viewMode: ViewMode;
  toggleViewMode: () => void;
}

const ViewModeContext = createContext<ViewModeContextValue | null>(null);

/** Pages that default to 'user' (simplified) mode on navigation */
const SIMPLIFIED_PATHS = [
  /^\/opinions\/search$/,
  /^\/opinions\/evaluation\/[^/]+\/run$/,
  /^\/civil-lawsuits\/trends\/pre-computed$/
];

function isSimplifiedPath(pathname: string): boolean {
  return SIMPLIFIED_PATHS.some(re => re.test(pathname));
}

export const ViewModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  const [viewMode, setViewMode] = useState<ViewMode>(
    isSimplifiedPath(location.pathname) ? 'user' : 'admin'
  );

  useEffect(() => {
    console.log('💡 View Mode: Press Ctrl+Shift+D to toggle between User and Admin modes');
  }, []);

  // Ctrl+Shift+D toggles on any page
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setViewMode(prev => {
          const newMode = prev === 'user' ? 'admin' : 'user';
          console.log(`View mode switched to: ${newMode}`);
          return newMode;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const toggleViewMode = () => {
    setViewMode(prev => (prev === 'user' ? 'admin' : 'user'));
  };

  return (
    <ViewModeContext.Provider value={{ viewMode, toggleViewMode }}>
      {children}
    </ViewModeContext.Provider>
  );
};

export const useViewMode = (): ViewModeContextValue => {
  const ctx = useContext(ViewModeContext);
  if (!ctx) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return ctx;
};
